import React from 'react'
import PropTypes from 'prop-types'
import {
  YAxis,
  XAxis,
  ResponsiveContainer,
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
} from 'recharts'
import { priceFormatter } from 'Utils'
import WithWindowBreakpoint, { BREAKPOINTS } from 'Components/WithWindowBreakpoint'
import { FONT_BASE_COLOR, CLICKABLE_COLOR, TERTIARY_COLOR, PRINT_TEXT_COLOR } from '@CSSVars/colors'
import { STAT_TARGET_KEYS } from '../SimpleChartsV2'
import { BASE_YEAR_WIDTH } from '../utils'

import { _AppLineChartContainer } from './styles'


const ActiveCustomizedLabel = (data) => {
  const {
    x, y, value, index, activeTooltipIndex,
    isBuilding, color, labelSvgLayerId,
  } = data
  if(index === activeTooltipIndex && value) {
    return (
      <g
        id={labelSvgLayerId}
        className="custom-tooltip"
        style={{ zIndex: '10000', transform: 'translateZ(10px)' }}
      >

        <rect
          width={12}
          height={12}
          x={isBuilding ? x + 21 : x - 14}
          y={y - 15}
          // dy={-4}
          fill={color}
          style={{
            transform: 'rotate(45deg) translateY(14px)',
            transformBox: 'fill-box',
            transformOrigin: 'center',
          }}
        />
        <rect
          width={75}
          height={30}
          x={isBuilding ? x + 15 : x - 90}
          y={y - 15}
          // dy={-4}
          fill={color}
        />
        <text
          x={isBuilding ? x + 52 : x - 52}
          y={y + 5}
          // dy={-4}
          fill={'white'}
          textAnchor="middle"
          style={{ fontSize: '12px' }}
        >

          {priceFormatter(value)}/sqft
        </text>
      </g>
    )
  }
  return ''
}

const CustomizedLabel = (data) => {
  const {
    x, y, value, index,
  } = data
  return (
    <text
      x={!index ? x + 12 : x}
      y={y - 5}
      dy={-4}
      fill={`${FONT_BASE_COLOR}`}
      fontSize={20}
      textAnchor="middle"
    >
      {priceFormatter(value)}
    </text>
  )
}

const CustomDot = (data) => {
  // Prevents overflow
  const { cx, cy, fill, isBuilding, index, activeTooltipIndex, printStyle } = data
  if((printStyle || isBuilding || index === activeTooltipIndex) && cy >= 10) {
    return (
      <circle
        key={`${cx} ${cy}`}
        cx={cx}
        cy={cy + 1}
        r={6}
        strokeWidth={2}
        stroke="white"
        fill={fill}
      />
    )
  }
  return ''
}

const CustomArea = ({
  isMaintChart = false,
  isBuilding,
  areaName,
  buildingName,
  printStyle,
  activeTooltipIndex,
  labelSvgLayerId,
  isMobileSize,
}) => {
  let name = `Avg. ${isBuilding ? buildingName || 'Building' : 'Area'} Price:`
  let dataKey = isBuilding ? STAT_TARGET_KEYS.BUILDING : STAT_TARGET_KEYS.AREA
  if(isMaintChart) {
    name = `Avg. ${
      isBuilding ? buildingName || 'Building' : areaName || 'Area'
    } Maintenance Fee:`
    dataKey = isBuilding
      ? STAT_TARGET_KEYS.BUILDING_MAINT
      : STAT_TARGET_KEYS.AREA_MAINT
  }

  const chartColor = isBuilding ? TERTIARY_COLOR : CLICKABLE_COLOR
  return (
    <Area
      connectNulls
      dot={
        <CustomDot
          fill={chartColor}
          isBuilding={isBuilding}
          activeTooltipIndex={activeTooltipIndex}
          printStyle={printStyle}
        />
      }
      name={name}
      key={isBuilding ? 'building' : 'area'}
      type="monotone"
      dataKey={dataKey}
      label={
        printStyle && isBuilding ?
          <CustomizedLabel /> : !isMobileSize && !printStyle ?
            <ActiveCustomizedLabel
              activeTooltipIndex={activeTooltipIndex}
              isBuilding={isBuilding}
              color={chartColor}
              labelSvgLayerId={labelSvgLayerId}
            />
            : ''
      }
      strokeWidth={isBuilding ? 2 : 0}
      stroke={chartColor}
      isAnimationActive={false}
      formatter={value => priceFormatter(value)}
      fill={chartColor}
      fillOpacity={isBuilding ? 0 : 0.1}
    />
  )
}

const AppChart = ({
  isMaintChart,
  areaName,
  buildingName,
  mergedChartData,
  onChartClick,
  getChartWrapperRef,
  currentLabel,
  onMouseMove,
  printStyle,
  activeTooltipIndex,
  winBreakpoint,
}) => {
  const labelSvgLayerId = isMaintChart ? 'maint-label-layer' : 'area-label-layer'

  const isMobileSize = winBreakpoint === BREAKPOINTS.XS || winBreakpoint === BREAKPOINTS.SM

  const customAreaProps = {
    isMaintChart,
    areaName,
    buildingName,
    printStyle,
    activeTooltipIndex,
    labelSvgLayerId,
    isMobileSize,
  }

  return (
    <_AppLineChartContainer
      getRef={getChartWrapperRef}
      forPrint={printStyle}
      key={isMaintChart ? `maint-${buildingName}-chart` : `area-${areaName}-chart`}
    >
      <ResponsiveContainer minWidth={mergedChartData.length * BASE_YEAR_WIDTH}>
        <AreaChart
          dot={false}
          width={730}
          margin={{
            top: printStyle ? 20 : 10,
            right: isMobileSize ? 10 : 90,
            left: printStyle ? 20 : isMobileSize ? -5 : 30,
            bottom: 5,
          }}
          data={mergedChartData}
          onClick={onChartClick}
          onMouseMove={onMouseMove}
          fill="#B8C4CA"
        >
          <CartesianGrid stroke="#D7D7D7" strokeDasharray="4 4" />
          <YAxis
            domain={['auto', 'auto']}
            tick={{
              dy: !printStyle ? 5 : 0,
              fill: FONT_BASE_COLOR,
            }}
            tickFormatter={priceFormatter}
            label={
              printStyle
                ? {
                  value: `${
                    isMaintChart ? 'Avg. Maint Fee / Sqft' : 'Avg. Price / Sqft'
                  }`,
                  angle: -90,
                  position: 'left',
                  offset: 14,
                  style: { textAnchor: 'middle' },
                  fill: PRINT_TEXT_COLOR,
                }
                : {}
            }
          />

          <XAxis
            dataKey={isMaintChart ? 'year' : 'name'}
            type={'category'}
            tick={{
              fill: FONT_BASE_COLOR,
            }}
            allowDuplicatedCategory={false}
          />

          {!printStyle && (
            <ReferenceLine
              x={currentLabel}
              stroke={TERTIARY_COLOR}
              strokeWidth={2}
              label=""
            />
          )}

          {isMaintChart
            ? [
              CustomArea({
                ...customAreaProps,
                isBuilding: false,
              }),
              CustomArea({
                ...customAreaProps,
                isBuilding: true,
              }),
            ]
            : [
              CustomArea({
                ...customAreaProps,
                isBuilding: false,
              }),
              CustomArea({
                ...customAreaProps,
                isBuilding: true,
              }),
              // for maint chart
            ]}

          {/* To bring the label svg layer to the top of all charts */}
          <use xlinkHref={`#${labelSvgLayerId}`} />

        </AreaChart>
      </ResponsiveContainer>
    </_AppLineChartContainer>
  )
}


CustomDot.propTypes = {
}

CustomArea.propTypes = {
  isMaintChart: PropTypes.bool,
  isBuilding: PropTypes.bool,
  areaName: PropTypes.string,
  buildingName: PropTypes.string,
  printStyle: PropTypes.bool,
  activeTooltipIndex: PropTypes.number,
  labelSvgLayerId: PropTypes.string,
  isMobileSize: PropTypes.bool,
}

AppChart.propTypes = {
  mergedChartData: PropTypes.arrayOf(PropTypes.shape({})),
  isMaintChart: PropTypes.bool,
  areaName: PropTypes.string,
  buildingName: PropTypes.string,
  currentLabel: PropTypes.string,
  onMouseMove: PropTypes.func,
  onChartClick: PropTypes.func,
  // need to get ref to move horizontal scroll to right to show latest year
  getChartWrapperRef: PropTypes.func,
  printStyle: PropTypes.bool,
  activeTooltipIndex: PropTypes.number,
  winBreakpoint: PropTypes.string,
}

export default WithWindowBreakpoint(AppChart)
